import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { lineString } from '@turf/helpers';
import along from '@turf/along';
import isEqual from 'lodash.isequal';
import { PrivacyPolicyOptions } from '@alltrails/privacy';
import type { ElevationPoint, LatLng } from '../types/Geo';
import type {
  CustomRouteDetails,
  CustomRouteSegmentId,
  CustomRouteSegment,
  CustomRouteWaypointId,
  CustomRouteWaypoint,
  CustomRouteMap,
  MarkerProperties,
  CustomRouteAlert,
  CustomRoutePath,
  Costings,
  CostingsConfig
} from '../types/CustomRoute';
import { ActivityType } from '../types/CustomRoute';
import { getLineColorBySegmentIndex, getNextColor } from '../utils/customRoutes/colors';
import { isSinglePointSegment } from '../utils/customRoutes/validation';
import { WAYPOINT_DECIMAL_PLACES } from '../utils/customRoutes/constants';
import type { Metadata, SegmentMetadata } from './customRoutesApi';

export const UNDO_LIMIT = 100;

export type ToastMessage = {
  iconCode?: string;
  message: string;
};

type RouteState = {
  segments: CustomRouteSegment[];
  activityType: ActivityType;
  costings?: Costings;
};

export type CustomRouteState = {
  isEditing?: boolean;
  isEditingWaypoint?: boolean;
  isSmartRouting: boolean;
  activeSegmentId?: CustomRouteSegmentId;
  hoveredSegmentId?: CustomRouteSegmentId;
  selectedHandleIndex?: number;
  nextSegmentId?: CustomRouteSegmentId;
  nextSegmentColor?: string;
  activeWaypointId?: CustomRouteWaypointId;
  selectedWaypointId?: CustomRouteWaypointId;
  activeWaypointPopupId?: CustomRouteWaypointId;
  nextWaypointId?: CustomRouteWaypointId;
  undoStack: RouteState[];
  redoStack: RouteState[];
  route: CustomRouteDetails;
  map?: CustomRouteMap;
  metadata?: { created: string; updated: string };
  toastMessage?: ToastMessage;
  toastAlerts: CustomRouteAlert[];
  draftWaypoint?: Partial<CustomRouteWaypoint> | null;
  costingsConfig?: CostingsConfig;
  costings?: Costings;
};

export const initialState: CustomRouteState = {
  isEditing: undefined,
  isEditingWaypoint: undefined,
  isSmartRouting: true,
  activeSegmentId: undefined,
  hoveredSegmentId: undefined,
  selectedHandleIndex: undefined,
  nextSegmentId: 0,
  nextSegmentColor: getLineColorBySegmentIndex(0),
  activeWaypointId: undefined,
  selectedWaypointId: undefined,
  activeWaypointPopupId: undefined,
  nextWaypointId: 0,
  undoStack: [],
  redoStack: [],
  route: {
    bounds: undefined,
    activityType: ActivityType.Hiking,
    id: undefined,
    distance: 0,
    elevationGain: 0,
    estimatedTime: undefined,
    segments: [],
    waypoints: [],
    createdAt: undefined,
    garminCourse: undefined,
    slug: '',
    name: '',
    description: '',
    contentPrivacy: undefined,
    location: {
      latitude: '',
      longitude: ''
    }
  },
  map: undefined,
  metadata: undefined,
  toastMessage: undefined,
  toastAlerts: [],
  costingsConfig: undefined,
  costings: undefined
};

export const selectIsDrawingEnabled = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.isEditingWaypoint,
  (state: { customRoute: CustomRouteState }) => state.customRoute.isEditing,
  (state: { customRoute: CustomRouteState }) => state.customRoute.activeSegmentId,
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (isEditingWaypoint, isEditing, activeSegmentId, segments) => {
    if (isEditingWaypoint) {
      return false;
    }

    if (!isEditing) {
      return false;
    }

    if (activeSegmentId !== undefined) {
      return true;
    }

    return segments.length === 0;
  }
);

export const selectCustomRouteActiveSegment = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.activeSegmentId,
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (activeSegmentId, segments) => segments.find(segment => segment.id === activeSegmentId)
);

export const selectCustomRouteInactiveSegments = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.activeSegmentId,
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (activeSegmentId, segments) => segments.filter(segment => segment.id !== activeSegmentId)
);

export const selectCustomRouteSegmentIds = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  segments => segments.map(segment => segment.id)
);

export const selectCustomRouteSegmentServerIds = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  segments => segments.map(segment => segment.serverId)
);

export const selectCustomRouteSegmentById = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (_: any, id: CustomRouteSegmentId) => id,
  (segments: CustomRouteSegment[], id: CustomRouteSegmentId) => segments.find(segment => segment.id === id)
);

export const selectCustomRouteSegmentsPaths = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (segments: CustomRouteSegment[]) => segments.map(segment => segment.paths)
);

export const selectCustomRouteElevationData = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.segments,
  (segments: CustomRouteSegment[]) => {
    let distanceBeforeCurrentSegment = 0;

    return segments.map(segment => {
      const elevationPoints = segment.elevationPoints?.map(
        ([distanceTo, ...rest]) => [distanceTo + distanceBeforeCurrentSegment, ...rest] as ElevationPoint
      );
      distanceBeforeCurrentSegment += segment.distance || 0;

      return elevationPoints || [];
    });
  }
);

export const selectCustomRouteWaypointIds = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.waypoints,
  waypoints => waypoints.map(waypoint => waypoint.id)
);

export const selectCustomRouteWaypointServerIds = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.waypoints,
  waypoints => waypoints.map(waypoint => waypoint.serverId)
);

export const selectCustomRouteWaypointById = createSelector(
  (state: { customRoute: CustomRouteState }) => state.customRoute.route.waypoints,
  (_: any, id: CustomRouteWaypointId) => id,
  (waypoints: CustomRouteWaypoint[], id: CustomRouteWaypointId) => waypoints.find(waypoint => waypoint.id === id)
);

export const getMarkerSelectionProperties = (
  marker: Pick<MarkerProperties, 'isStart' | 'isEnd' | 'pathIndex' | 'endPathIndex'>,
  selectedHandleIndex?: number
) => {
  const { isStart, isEnd, pathIndex, endPathIndex } = marker;
  const isStartEnd = isStart && isEnd;
  const isCombinedStart = isStartEnd && selectedHandleIndex === 0;
  const isCombinedEnd = isStartEnd && selectedHandleIndex !== 0;
  const isStartOnly = isStart && !isEnd;
  const isEndOnly = !isStart && isEnd;

  return { isStart: isStartOnly || isCombinedStart, isEnd: isEndOnly || isCombinedEnd, pathIndex: isCombinedEnd ? endPathIndex! : pathIndex };
};

const updateRouteMetadata = (state: CustomRouteState) => {
  if (!state.route.segments.length || state.route.segments.every(segment => segment.paths.length === 0 || isSinglePointSegment(segment))) {
    state.route.distance = 0;
    state.route.elevationGain = 0;
    state.route.estimatedTime = undefined;
  }
};

const updateSegmentMetadata = (segment: CustomRouteSegment) => {
  // Reset segment metadata if the last path was removed or the last path was reduced to one point
  if (segment.paths.length === 0 || isSinglePointSegment(segment)) {
    segment.elevationPoints = [];
    segment.distance = 0;
    segment.elevationGain = 0;
    segment.estimatedTime = undefined;
  }
};

const updateActiveSegmentId = (state: CustomRouteState) => {
  if (state.activeSegmentId === undefined) {
    return;
  }

  const segment = state.route.segments[state.activeSegmentId];
  if (!segment) {
    state.activeSegmentId = undefined;
    return;
  }
};

const updateSelectedHandleIndex = (state: CustomRouteState, markerIndex?: number) => {
  if (!state.isEditing || state.activeSegmentId === undefined) {
    state.selectedHandleIndex = undefined;
    return;
  }

  const activeSegment = state.route.segments[state.activeSegmentId];
  if (!activeSegment || activeSegment.paths.length === 0) {
    state.selectedHandleIndex = undefined;
    return;
  }

  const paths = activeSegment.paths;
  const isSinglePoint = isSinglePointSegment(activeSegment);
  // If the segment has more than one point select the end point whose index is one more than the number of paths
  const endMarkerIndex = isSinglePoint ? 0 : paths.length;

  // If the marker index is not defined or is out of bounds, select the end point
  state.selectedHandleIndex = markerIndex !== undefined && markerIndex >= 0 && markerIndex <= endMarkerIndex ? markerIndex : endMarkerIndex;
};

const updateNextSegment = (state: CustomRouteState) => {
  const segments = state.route.segments;
  state.nextSegmentId = segments.length > 0 ? segments.reduce((max, segment) => (segment.id > max ? segment.id : max), 0) + 1 : 0;
  state.nextSegmentColor = segments.length > 0 ? getNextColor(segments[segments.length - 1].color) : getLineColorBySegmentIndex(0);
};

const updateNextWaypoint = (state: CustomRouteState) => {
  const waypoints = state.route.waypoints;
  state.nextWaypointId = waypoints.length > 0 ? waypoints.reduce((max, waypoint) => (waypoint.id > max ? waypoint.id : max), 0) + 1 : 0;
};

export const updateUndoRedoStacks = (state: CustomRouteState) => {
  // Since we might edit segments in place after updating the undo stack we need to save a deep copy to the undo stack
  state.undoStack.push(
    JSON.parse(
      JSON.stringify({
        segments: state.route.segments,
        activityType: state.route.activityType,
        costings: state.costings
      })
    )
  );
  state.redoStack = [];

  if (state.undoStack.length > UNDO_LIMIT) {
    state.undoStack.shift();
  }
};

const resetCostings = (state: CustomRouteState) => {
  const costingsConfig = state.costingsConfig;

  if (!costingsConfig) {
    return;
  }

  state.costings = Object.keys(costingsConfig).reduce<Costings>(
    (acc, key) => {
      acc[key] = costingsConfig[key].default;
      return acc;
    },
    { leastGain: 0, mostScenic: 0, mostPopular: 0, makeShorter: 0 }
  );
};

export const customRouteSlice = createSlice({
  name: 'customRoute',
  initialState,
  reducers: {
    resetState: (
      state: CustomRouteState,
      action: PayloadAction<{ persistKeys?: (keyof CustomRouteState)[]; initialState?: Partial<CustomRouteState> }>
    ) => {
      const defaultState: CustomRouteState = { ...initialState, ...(action.payload.initialState || {}) };
      action.payload.persistKeys?.forEach(key => {
        const persistValue = state[key];
        (defaultState[key] as typeof persistValue) = persistValue;
      });

      return defaultState;
    },
    setEditState: (state, action: PayloadAction<{ isEditing: boolean; activeSegmentId?: CustomRouteSegmentId }>) => {
      const { isEditing, activeSegmentId } = action.payload;
      const wasEditing = state.isEditing;
      state.isEditing = isEditing;

      if (activeSegmentId !== undefined) {
        state.activeSegmentId = activeSegmentId;
      }

      if (!isEditing) {
        state.activeSegmentId = undefined;
        resetCostings(state);
      }

      if (!isEditing || (isEditing && !wasEditing)) {
        updateSelectedHandleIndex(state);
      }
    },
    setIsSmartRouting: (state, action: PayloadAction<boolean>) => {
      state.isSmartRouting = action.payload;
    },
    setRoute: (
      state,
      action: PayloadAction<{
        route: CustomRouteDetails;
        map?: CustomRouteMap;
        persistEditState?: boolean;
      }>
    ) => {
      const { route, map, persistEditState } = action.payload;
      state.route = route;
      state.map = map;

      if (map?.metadata) {
        state.metadata = map.metadata;
      }

      state.route.segments.forEach(segment => {
        if (!segment.color) {
          segment.color = getLineColorBySegmentIndex(segment.id);
        }
      });

      if (!persistEditState) {
        state.isEditing = false;
      }
      state.activeSegmentId = undefined;
      state.selectedHandleIndex = undefined;
      state.activeWaypointId = undefined;
      state.selectedWaypointId = undefined;

      updateNextSegment(state);
      updateNextWaypoint(state);
      resetCostings(state);
    },
    updateRoute: (
      state,
      action: PayloadAction<{
        route: CustomRouteDetails;
        map: CustomRouteMap;
      }>
    ) => {
      const { route, map } = action.payload;
      state.map = map;

      const { id, slug, name, description, segments, waypoints, createdAt } = route;

      // Fields that will never be more up to date locally than on the server
      state.route.id = id;
      state.route.slug = slug;
      state.route.createdAt = createdAt;

      // Fields that if not set locally will be set to the server value
      state.route.name ||= name;
      state.route.description ||= description;

      // Add any new segments and waypoints to the route after any existing segments and waypoints

      const localSegmentServerIds = state.route.segments.map(segment => segment.serverId);
      const newSegments = segments.filter(segment => !localSegmentServerIds.includes(segment.serverId));
      newSegments.forEach(segment => {
        const newSegment = { ...segment, id: state.nextSegmentId!, color: segment.color || state.nextSegmentColor! };
        state.route.segments.push(newSegment);
        updateNextSegment(state);
      });

      const localWaypointServerIds = state.route.waypoints.map(waypoint => waypoint.serverId);
      const newWaypoints = waypoints.filter(waypoint => !localWaypointServerIds.includes(waypoint.serverId));
      newWaypoints.forEach(waypoint => {
        // longitude and latitude round to 6 decimal places
        const latitude = Number(waypoint.latitude.toFixed(WAYPOINT_DECIMAL_PLACES));
        const longitude = Number(waypoint.longitude.toFixed(WAYPOINT_DECIMAL_PLACES));
        const newWaypoint = { ...waypoint, id: state.nextWaypointId!, latitude, longitude };
        state.route.waypoints.push(newWaypoint);
        updateNextWaypoint(state);

        return newWaypoint;
      });

      resetCostings(state);
    },
    setActiveSegmentId: (state, action: PayloadAction<{ activeSegmentId: CustomRouteSegmentId | undefined; canEdit: boolean }>) => {
      const { activeSegmentId, canEdit } = action.payload;
      state.activeSegmentId = activeSegmentId;

      if (activeSegmentId !== undefined && canEdit) {
        state.isEditing = true;
      }

      updateSelectedHandleIndex(state);
      resetCostings(state);
    },
    setHoveredSegmentId: (state, action: PayloadAction<CustomRouteSegmentId | undefined>) => {
      state.hoveredSegmentId = action.payload;
    },
    setSelectedHandleIndex: (state, action: PayloadAction<number | undefined>) => {
      state.selectedHandleIndex = action.payload;
    },
    updateRouting: (
      state,
      action: PayloadAction<{ activityType?: ActivityType; segment?: CustomRouteSegment; segments?: CustomRouteSegment[]; costings?: Costings }>
    ) => {
      const { activityType, segment, segments, costings } = action.payload;

      if (!activityType && !segment && !segments && !costings) {
        return;
      }

      updateUndoRedoStacks(state);

      if (activityType) {
        state.route.activityType = activityType;
      }

      // If a segment is provided, update the corresponding segment in the route
      if (segment) {
        const currentSegment = state.route.segments.find(s => s.id === segment.id);
        if (currentSegment) {
          Object.assign(currentSegment, segment);
        }
      }

      // If an array of segments is provided, overwrite the existing segments with the new segments
      if (segments) {
        state.route.segments = segments;
        updateNextSegment(state);
        updateRouteMetadata(state);
        updateSelectedHandleIndex(state);
      }

      if (costings) {
        state.costings = costings;
      } else {
        resetCostings(state);
      }
    },
    setRouteDetails: (state, action: PayloadAction<{ name: string; description?: string; contentPrivacy: PrivacyPolicyOptions }>) => {
      const { name, description, contentPrivacy } = action.payload;
      state.isEditing = true;
      state.route.name = name;
      state.route.description = description;
      state.route.contentPrivacy = contentPrivacy;
    },
    setRoutePrivacy: (state, action: PayloadAction<CustomRouteMap['contentPrivacy']>) => {
      state.route.contentPrivacy = action.payload;
    },
    addSegment: (state, action: PayloadAction<Partial<Omit<CustomRouteSegment, 'id' | 'color'>> | undefined>) => {
      updateUndoRedoStacks(state);
      state.isEditing = true;

      const id = state.nextSegmentId || 0;
      const color = state.nextSegmentColor || getLineColorBySegmentIndex(0);
      const distance = action.payload?.distance || 0;
      const elevationGain = action.payload?.elevationGain || 0;
      const elevationPoints = action.payload?.elevationPoints || [];
      const paths = action.payload?.paths || [];
      state.route.segments.push({
        id,
        color,
        distance,
        elevationGain,
        elevationPoints,
        paths
      });

      state.activeSegmentId = id;
      state.nextSegmentId = id + 1;
      state.nextSegmentColor = getNextColor(color);

      updateSelectedHandleIndex(state);
      resetCostings(state);
    },
    removeSegment: (state, action: PayloadAction<CustomRouteSegmentId>) => {
      const removeId = action.payload;
      const segment = state.route.segments.find(segment => segment.id === removeId);

      if (!segment) {
        return;
      }

      updateUndoRedoStacks(state);
      state.isEditing = true;

      state.route.segments = state.route.segments.reduce((acc, segment) => {
        if (segment.id === removeId) {
          return acc;
        }

        if (segment.id > removeId) {
          // Re-index the segments after the removed segment
          segment.id = segment.id - 1;
        }

        acc.push(segment);
        return acc;
      }, [] as CustomRouteSegment[]);

      // Update the next segment id
      state.nextSegmentId = state.route.segments.length > 0 ? state.route.segments[state.route.segments.length - 1].id + 1 : 0;

      // If the last segment was removed update the next color based on the new last segment
      if (removeId === state.route.segments.length) {
        state.nextSegmentColor =
          state.route.segments.length > 0 ? getNextColor(state.route.segments[state.route.segments.length - 1].color) : getLineColorBySegmentIndex(0);
      }

      if (state.activeSegmentId === removeId) {
        // If the active segment was removed, clear the active segment and reset selected handle
        state.activeSegmentId = undefined;
        updateSelectedHandleIndex(state);
      } else if (state.activeSegmentId !== undefined && state.activeSegmentId > removeId) {
        // If the active segment is after the removed segment, re-index it
        state.activeSegmentId = state.activeSegmentId - 1;
      }

      updateRouteMetadata(state);
      resetCostings(state);
    },
    setSegmentColor: (
      state,
      action: PayloadAction<{
        id: CustomRouteSegmentId;
        color: string;
      }>
    ) => {
      const { id, color } = action.payload;
      const segment = state.route.segments.find(segment => segment.id === id);

      if (!segment) {
        return;
      }

      updateUndoRedoStacks(state);
      state.isEditing = true;
      segment.color = color;

      // If the segment being updated is the last segment, update the next segment color
      if (id === state.route.segments.length - 1) {
        state.nextSegmentColor = getNextColor(color);
      }
    },
    addActiveSegmentPath: (state, action: PayloadAction<CustomRoutePath>) => {
      const { points, freeDraw } = action.payload;
      const activeSegmentId = state.activeSegmentId;

      if (activeSegmentId === undefined) {
        return;
      }

      const activeSegment = state.route.segments[activeSegmentId];

      if (!activeSegment) {
        return;
      }

      const paths = activeSegment.paths;
      const selectedHandleIndex = state.selectedHandleIndex;

      if (paths.length > 0 && (selectedHandleIndex === undefined || selectedHandleIndex > paths.length)) {
        return;
      }

      updateUndoRedoStacks(state);

      let newSelectedHandleIndex;

      if (paths.length === 0) {
        // If this is the first point in the segment, create a new path
        paths.push({ points, freeDraw });
      } else if (isSinglePointSegment(activeSegment)) {
        // If the active segment has only one path with one point, add the points of the new path to that path
        paths[0].points.push(...points);
        paths[0].freeDraw = freeDraw;
      } else {
        const isStart = selectedHandleIndex === 0;
        const isEnd = selectedHandleIndex === paths.length;
        // The end handle is at the end of the last path rather than at the beginning of a path
        const pathIndex = isEnd ? selectedHandleIndex - 1 : selectedHandleIndex!;
        const path = paths[pathIndex];
        const newPoints = [...points];

        // If the last point of the new path is not the first point of the existing path, append the first point of the existing path to the new path
        if (isStart && !isEqual(newPoints[newPoints.length - 1], path.points[0])) {
          newPoints.push(path.points[0]);
        } else if (isEnd && !isEqual(newPoints[0], path.points[path.points.length - 1])) {
          // If the first point of the new path is not the last point of the existing path, prepend the last point of the existing path to the new path
          newPoints.unshift(path.points[path.points.length - 1]);
        } else if (!isStart && !isEnd && !isEqual(newPoints[0], path.points[0])) {
          // If the first point of the new path is not the first point of the existing path, prepend the first point of the existing path to the new path
          newPoints.unshift(path.points[0]);
        }

        // Insert the new path at the selected handle index
        paths.splice(selectedHandleIndex!, 0, { points: newPoints, freeDraw });

        // If not inserting at the start or end, also insert a reverse path after the new path so that the final result is that an out-and-back path has been inserted before the selected handle
        if (!isStart && !isEnd) {
          paths.splice(pathIndex + 1, 0, { points: [...newPoints].reverse(), freeDraw });
        }

        // Select the newly placed handle
        // If the start handle was selected a new start handle was inserted, so select it
        // For any other handle the new handle is the next handle after the selected handle
        newSelectedHandleIndex = isStart ? 0 : selectedHandleIndex! + 1;
      }

      updateSelectedHandleIndex(state, newSelectedHandleIndex);
      resetCostings(state);
    },
    addActiveSegmentHandle: (
      state,
      action: PayloadAction<{ pathIndex: number; pointIndex: number; latitude: number; longitude: number; replacementPoints?: LatLng[] }>
    ) => {
      const { pathIndex, pointIndex, latitude, longitude, replacementPoints } = action.payload;

      if (state.activeSegmentId === undefined) {
        return;
      }

      const segment = state.route.segments[state.activeSegmentId];
      if (!segment) {
        return;
      }

      const path = segment.paths[pathIndex];
      if (!path) {
        return;
      }

      const points = replacementPoints || path.points;
      if (pointIndex > points.length - 1) {
        return;
      }

      updateUndoRedoStacks(state);

      const sliceIndex = pointIndex === points.length - 1 ? pointIndex : pointIndex + 1;
      const beforePoints = points.slice(0, sliceIndex);
      const afterPoints = points.slice(sliceIndex);

      segment.paths.splice(
        pathIndex,
        1,
        { points: [...beforePoints, [latitude, longitude]], freeDraw: path.freeDraw },
        {
          points: [[latitude, longitude], ...afterPoints],
          freeDraw: path.freeDraw
        }
      );

      // Select the new handle
      updateSelectedHandleIndex(state, pathIndex + 1);
      resetCostings(state);
    },
    updateActiveSegmentHandle: (
      state,
      action: PayloadAction<
        Omit<MarkerProperties, 'id' | 'segmentId'> & {
          beforePoints?: LatLng[];
          afterPoints?: LatLng[];
        }
      >
    ) => {
      const { latitude, longitude, beforePoints: rawBeforePoints, afterPoints: rawAfterPoints } = action.payload;
      const { isStart, isEnd, pathIndex } = getMarkerSelectionProperties(action.payload, state.selectedHandleIndex);

      if (state.activeSegmentId === undefined) {
        return;
      }

      const segment = state.route.segments[state.activeSegmentId];
      if (!segment) {
        return;
      }

      const paths = segment.paths;
      const path = paths[pathIndex];
      if (!path) {
        return;
      }

      updateUndoRedoStacks(state);

      let prevPath;
      let beforePoints = rawBeforePoints ? [...rawBeforePoints] : undefined;
      let afterPoints = rawAfterPoints ? [...rawAfterPoints] : undefined;

      // Determine the previous path, before points, and after points based on the type of handle being updated
      if (isStart) {
        afterPoints ||= [[latitude, longitude], path.points[path.points.length - 1]];
      } else if (isEnd) {
        prevPath = paths[pathIndex];
        beforePoints ||= [path.points[0], [latitude, longitude]];
      } else {
        prevPath = paths[pathIndex - 1];
        beforePoints ||= [prevPath.points[0], [latitude, longitude]];
        afterPoints ||= [[latitude, longitude], path.points[path.points.length - 1]];
      }

      // Update the points before the handle
      if (beforePoints) {
        // If the first point of the new previous path is not the first point of the old previous path, insert the first point of the old previous path into the beginning of the new previous path
        if (!isEqual(beforePoints[0], prevPath.points[0])) {
          beforePoints.unshift(prevPath.points[0]);
        }

        prevPath.points = beforePoints;
        prevPath.freeDraw = !rawBeforePoints;
      }

      // Update the points after the handle
      if (afterPoints) {
        // If the last point of the new path is not the first point of the next path, insert the first point of the next path into the end of the new path
        if (!isEqual(afterPoints[afterPoints.length - 1], path.points[path.points.length - 1])) {
          afterPoints.push(path.points[path.points.length - 1]);
        }

        path.points = afterPoints;
        path.freeDraw = !rawAfterPoints;
      }

      // Note that we intentionally do not update the selected handle index after updating a segment handle since it should remain the same
      resetCostings(state);
    },
    removeActiveSegmentHandle: (state, action: PayloadAction<LatLng[] | undefined>) => {
      const rawReplacementPoints = action.payload;

      const activeSegmentId = state.activeSegmentId;
      if (activeSegmentId === undefined) {
        return;
      }

      const segment = state.route.segments[activeSegmentId];
      if (!segment) {
        return;
      }

      const selectedHandleIndex = state.selectedHandleIndex;
      if (selectedHandleIndex === undefined || selectedHandleIndex > segment.paths.length) {
        return;
      }

      const isStart = selectedHandleIndex === 0;
      const isEnd = selectedHandleIndex === segment.paths.length;
      const pathIndex = isEnd ? segment.paths.length - 1 : selectedHandleIndex;
      const path = segment.paths[pathIndex];
      if (!path) {
        return;
      }

      updateUndoRedoStacks(state);

      if (segment.paths.length === 1 && path.points.length > 1) {
        // If the segment only has one path and it has more than one point there are only start and end handles
        // If removing the start handle remove all but the end point
        // If removing the end handle remove all but the start point
        segment.paths[0].points = isStart ? [path.points[path.points.length - 1]] : [path.points[0]];
      } else if (isStart || isEnd) {
        // If removing the start or end point, remove the path at the specified index
        segment.paths.splice(pathIndex, 1);
      } else {
        // If removing a handle, replace the path at the specified index and the path before it with a new path connecting the first point of the path before it to the last point of the path at the specified index
        // Since this is not the start point, we know that the path before it exists
        const startPoint = segment.paths[pathIndex - 1].points[0];
        const endPoint = segment.paths[pathIndex].points[segment.paths[pathIndex].points.length - 1];
        const replacementPoints = rawReplacementPoints ? [...rawReplacementPoints] : [startPoint, endPoint];

        // If the replacement points don't start with the start point, add it to the beginning of the path
        if (!isEqual(replacementPoints[0], startPoint)) {
          replacementPoints.unshift(startPoint);
        }

        // If the replacement points don't end with the end point, add it to the end of the path
        if (!isEqual(replacementPoints[replacementPoints.length - 1], endPoint)) {
          replacementPoints.push(endPoint);
        }

        // Remove the path at the specified index
        segment.paths.splice(pathIndex, 1);

        if (replacementPoints.every(point => isEqual(point, replacementPoints[0]))) {
          // If the replacement points are all the same, remove the previous path as well
          segment.paths.splice(pathIndex - 1, 1);
        } else {
          // Otherwise replace the path before the removed path with the new path
          segment.paths[pathIndex - 1] = { points: replacementPoints, freeDraw: !rawReplacementPoints };
        }
      }

      updateSegmentMetadata(segment);
      updateRouteMetadata(state);
      // If the start point was removed select the new start point, else select the handle before the removed handle
      updateSelectedHandleIndex(state, selectedHandleIndex === 0 ? 0 : selectedHandleIndex - 1);
      resetCostings(state);
    },
    reverseActiveSegment: state => {
      const segmentId = state.activeSegmentId;
      if (segmentId === undefined) {
        return;
      }

      const segment = state.route.segments[segmentId];
      if (!segment) {
        return;
      }

      updateUndoRedoStacks(state);

      segment.paths.reverse();
      segment.paths.forEach(path => {
        path.points.reverse();
      });

      // Note that we intentionally do not update the selected handle index after reversing a segment since it should remain the same
      resetCostings(state);
    },
    doubleBackActiveSegment: state => {
      const segmentId = state.activeSegmentId;
      if (segmentId === undefined) {
        return;
      }

      const segment = state.route.segments[segmentId];

      // If the segment has fewer than 2 points, do nothing
      if (!segment || segment.paths.length === 0 || segment.paths[0].points.length < 2) {
        return;
      }

      const startPoint = segment.paths[0].points[0];
      const endPath = segment.paths[segment.paths.length - 1];
      const endPoint = endPath.points[endPath.points.length - 1];
      // if the start and end points are the same, do nothing
      if (isEqual(startPoint, endPoint)) {
        return;
      }

      updateUndoRedoStacks(state);

      const returnPaths = [...segment.paths.map(path => ({ ...path, points: [...path.points] }))];
      returnPaths.reverse();
      returnPaths.forEach(path => {
        path.points.reverse();
      });
      segment.paths.push(...returnPaths);

      // Select the new end point of the segment
      updateSelectedHandleIndex(state, segment.paths.length);
      resetCostings(state);
    },
    closeLoopActiveSegment: (state, action: PayloadAction<LatLng[] | undefined>) => {
      const rawPoints = action.payload;

      const segmentId = state.activeSegmentId;
      if (segmentId === undefined) {
        return;
      }

      const segment = state.route.segments[segmentId];

      // If the segment has fewer than 2 points, do nothing
      if (!segment || segment.paths.length === 0 || segment.paths[0].points.length < 2) {
        return;
      }

      const startPoint = segment.paths[0].points[0];
      const endPath = segment.paths[segment.paths.length - 1];
      const endPoint = endPath.points[endPath.points.length - 1];

      // If the start and end points are the same, do nothing
      if (isEqual(startPoint, endPoint)) {
        return;
      }

      updateUndoRedoStacks(state);

      const points = rawPoints ? [...rawPoints] : [endPoint, startPoint];

      // If the first point of the new path is not the last point of the previous path, insert the last point of the previous path into the beginning of the new path
      if (!isEqual(points[0], endPoint)) {
        points.unshift(endPoint);
      }

      // If the last point of the new path is not the start point, insert the start point into the end of the new path
      if (!isEqual(points[points.length - 1], startPoint)) {
        points.push(startPoint);
      }

      const path = { points, freeDraw: !rawPoints };
      segment.paths.push(path);

      // Select the new end point of the segment
      updateSelectedHandleIndex(state, segment.paths.length);
      resetCostings(state);
    },
    updateMetadata: (state, action: PayloadAction<Metadata & { segmentsMetadata: Record<number, SegmentMetadata> }>) => {
      const { segmentsMetadata, elevationGain, length: distance, averageTimeToComplete: estimatedTime } = action.payload;

      // Note that we intentionally don't add this to the undo stack.
      // Since metadata is updated as a side effect of modifying segments performing an undo or redo will cause the metadata to be updated for the restored segments
      // Since metadata fetching is cached if the restored segments have already had metadata fetched we will use the cached metadata

      Object.keys(segmentsMetadata).forEach(stringIndex => {
        const index = Number(stringIndex);
        const segmentMetadata = segmentsMetadata[index];
        const { elevations, elevationGain, length: distance, averageTimeToComplete: estimatedTime, startLocation } = segmentMetadata;
        const segment = state.route.segments[index];
        if (segment) {
          let elevationPoints: ElevationPoint[] | undefined;
          const points = segment.paths
            .map(path => path.points)
            .flat()
            .map(([lat, lng]) => [lng, lat]);

          // We can only calculate elevation points if there are at least two points in the segment
          if (points.length > 1) {
            // Add the total distance before the active segment to each point in the active segment returned from the API and interpolate the coordinates
            const line = lineString(
              segment.paths
                .map(path => path.points)
                .flat()
                .map(([lat, lng]) => [lng, lat])
            );
            elevationPoints = elevations?.map((elevation, index) => {
              const distanceTo = (index / (elevations.length - 1)) * (distance || 0);
              const coordinates = along(line, distanceTo, { units: 'meters' }).geometry.coordinates;
              return [distanceTo, elevation, [coordinates[1], coordinates[0]]] as ElevationPoint;
            });
          }

          segment.elevationPoints = elevationPoints;
          segment.distance = distance ?? undefined;
          segment.elevationGain = elevationGain ?? undefined;
          segment.estimatedTime = estimatedTime || undefined;
          segment.startLocation = startLocation;
        }
      });

      state.route.elevationGain = elevationGain ?? undefined;
      state.route.distance = distance ?? undefined;
      state.route.estimatedTime = estimatedTime || undefined;
    },

    setWaypointEditingState: (
      state,
      action: PayloadAction<{
        activeId?: CustomRouteWaypointId | undefined;
        selectedId?: CustomRouteWaypointId | undefined;
        isEditing?: boolean;
      }>
    ) => {
      const { activeId, selectedId, isEditing } = action.payload;
      const isEditingWaypoint = isEditing ?? selectedId !== undefined;
      state.isEditingWaypoint = isEditingWaypoint;
      state.selectedWaypointId = selectedId;
      state.activeWaypointId = selectedId === undefined ? activeId : undefined;

      if (isEditingWaypoint) {
        state.isEditing = true;
      }
    },
    clearWaypointEditingState: state => {
      if (state.selectedWaypointId !== undefined) {
        state.selectedWaypointId = undefined;
      }
      if (state.activeWaypointId !== undefined) {
        state.activeWaypointId = undefined;
      }
      if (state.isEditingWaypoint) {
        state.isEditingWaypoint = false;
      }
    },
    setActiveWaypointPopup: (state, action: PayloadAction<CustomRouteWaypointId>) => {
      state.activeWaypointPopupId = action.payload;
    },
    clearActiveWaypointPopup: state => {
      state.activeWaypointPopupId = undefined;
    },
    addWaypoint: (state, action: PayloadAction<Omit<CustomRouteWaypoint, 'id'>>) => {
      state.isEditing = true;

      const id = state.nextWaypointId || 0;
      state.route.waypoints.push({ ...action.payload, id });

      state.activeWaypointId = id;
      state.selectedWaypointId = id;
      state.nextWaypointId = id + 1;
    },
    updateWaypoint: (
      state,
      action: PayloadAction<{
        id: CustomRouteWaypointId;
        name: string;
        description: string;
        latitude: number;
        longitude: number;
        showTitle: boolean;
      }>
    ) => {
      state.isEditing = true;

      const { id, ...updates } = action.payload;

      const waypointIndex = state.route.waypoints.findIndex(waypoint => waypoint.id === id);
      if (waypointIndex !== -1) {
        state.route.waypoints[waypointIndex] = {
          ...state.route.waypoints[waypointIndex],
          ...updates
        };
      }
    },
    updateWaypointDraft: (state, action: PayloadAction<Partial<CustomRouteWaypoint> | null>) => {
      state.draftWaypoint = action.payload;
    },
    removeWaypoint: (state, action: PayloadAction<CustomRouteWaypointId>) => {
      const removeId = action.payload;
      state.route.waypoints = state.route.waypoints.reduce((acc, waypoint) => {
        if (waypoint.id === removeId) {
          return acc;
        }

        // Re-index the waypoints after the removed waypoint
        const newId = waypoint.id > removeId ? waypoint.id - 1 : waypoint.id;
        acc.push({ ...waypoint, id: newId });
        return acc;
      }, [] as CustomRouteWaypoint[]);

      // Update the next waypoint id
      state.nextWaypointId = state.route.waypoints.length > 0 ? state.route.waypoints[state.route.waypoints.length - 1].id + 1 : 0;

      // If the last waypoint was removed, clear the active waypoint
      if (state.activeWaypointId === removeId) {
        state.activeWaypointId = undefined;
      }
    },
    setToastMessage: (state, action: PayloadAction<ToastMessage | undefined>) => {
      state.toastMessage = action.payload;
    },
    setToastAlerts: (state, action: PayloadAction<CustomRouteAlert[]>) => {
      state.toastAlerts = [...action.payload];
    },
    clearTopToastAlert: state => {
      state.toastAlerts.shift();
    },
    undo: state => {
      const prevState = state.undoStack?.pop();
      if (!prevState) {
        return;
      }

      // Save current state to redo stack
      state.redoStack.push({
        segments: state.route.segments,
        activityType: state.route.activityType,
        costings: state.costings
      });

      // Restore state from undo stack
      state.route.segments = prevState.segments;
      state.route.activityType = prevState.activityType;
      state.costings = prevState.costings;
      updateNextSegment(state);
      updateRouteMetadata(state);
      updateActiveSegmentId(state);
      updateSelectedHandleIndex(state);
    },
    redo: state => {
      const nextState = state.redoStack?.pop();
      if (!nextState) {
        return;
      }

      // Save current state to undo stack
      state.undoStack.push({
        segments: state.route.segments,
        activityType: state.route.activityType,
        costings: state.costings
      });

      // Restore state from redo stack
      state.route.segments = nextState.segments;
      state.route.activityType = nextState.activityType;
      state.costings = nextState.costings;
      updateNextSegment(state);
      updateRouteMetadata(state);
      updateActiveSegmentId(state);
      updateSelectedHandleIndex(state);
    },
    resetUndoRedo: state => {
      state.undoStack = [];
      state.redoStack = [];
    },
    setCostingsConfig: (state, action: PayloadAction<CostingsConfig>) => {
      state.costingsConfig = action.payload;
      resetCostings(state);
    },
    setCostings: (state, action: PayloadAction<Costings>) => {
      state.costings = action.payload;
    }
  }
});

export const {
  resetState,
  setEditState,
  setIsSmartRouting,
  setRoute,
  updateRoute,
  updateRouting,
  setRouteDetails,
  setRoutePrivacy,
  setActiveSegmentId,
  setHoveredSegmentId,
  setSelectedHandleIndex,
  addSegment,
  removeSegment,
  setSegmentColor,
  addActiveSegmentPath,
  addActiveSegmentHandle,
  updateActiveSegmentHandle,
  removeActiveSegmentHandle,
  reverseActiveSegment,
  doubleBackActiveSegment,
  closeLoopActiveSegment,
  updateMetadata,
  setWaypointEditingState,
  clearWaypointEditingState,
  setActiveWaypointPopup,
  clearActiveWaypointPopup,
  addWaypoint,
  updateWaypoint,
  removeWaypoint,
  updateWaypointDraft,
  setToastMessage,
  setToastAlerts,
  clearTopToastAlert,
  undo,
  redo,
  resetUndoRedo,
  setCostingsConfig,
  setCostings
} = customRouteSlice.actions;

export const customRouteReducer = customRouteSlice.reducer;
